import { InfrastructureEntity } from '../types/InfrastructureEntity';
import { StorageEntity } from '../types/StorageEntity';
import { PipelineEntity } from '../types/PipelineEntity';

export function defineInfrastructure(): (InfrastructureEntity | StorageEntity | PipelineEntity)[] {
  return [
    new InfrastructureEntity({
      id: 'webScraper',
      name: 'Web Scraper',
      cost: 100,
      consumes: { money: 0.05 },
      produces: { rawData: 1 },
      description: 'Automated web scraping system. Produces 1 raw data/sec. First step to data collection',
      progressionRequirements: {
        upgrades: ['automation']
      },
      failureRate: 0.005,
      maxCount: 20
    }),

    new InfrastructureEntity({
      id: 'dataCleaner',
      name: 'Data Cleaning Script',
      cost: 80,
      description: 'Automated data cleaning script. Converts raw data to clean data (1:1 ratio). Enables data monetization',
      consumes: { money: 0.02, rawData: 1 },
      produces: { cleanData: 1 },
      progressionRequirements: {
        upgrades: ['automation', 'dataCleaning'],
        infrastructure: { webScraper: 1 }
      },
      failureRate: 0.008,
      maxCount: 20
    }),

    new InfrastructureEntity({
      id: 'cleanDataApi',
      name: 'Clean Data API',
      cost: 400,
      description: 'Premium API for selling cleaned, validated data - sustainable revenue stream',
      consumes: { money: 0.1, cleanData: 3 },
      produces: { money: 5.0 },
      progressionRequirements: {
        upgrades: ['dataMarketplace', 'dataCleaning']
      },
      failureRate: 0.020
    }),

    new InfrastructureEntity({
      id: 'iotSensor',
      name: 'IoT Sensors',
      cost: 600,
      consumes: { money: 1 },
      produces: { rawData: 50 },
      description: 'High-volume IoT sensor network. Produces 50 raw data/sec. Scales data collection significantly',
      progressionRequirements: {
        personnel: { dataEngineer: 1 },
        upgrades: ['streaming']
      },
      failureRate: 0.02
    }),

    new InfrastructureEntity({
      id: 'socialMediaMonitor',
      name: 'Social Media Monitor',
      cost: 1,
      consumes: { money: 1 },
      produces: { rawData: 25 },
      storage: { rawData: 150 },
      description: 'Social media monitoring with sentiment analysis. Produces 25 raw data/sec + 150 storage capacity',
      progressionRequirements: {
        upgrades: ['socialNetworks', 'streaming']
      },
      failureRate: 0.015
    }),

    new InfrastructureEntity({
      id: 'thoughtPoliceScanner',
      name: 'Thought Police Scanner',
      cost: 1,
      consumes: { money: 1 },
      produces: { rawData: 100 },
      storage: { rawData: 500 },
      description: 'Thoughtcrime does not entail death: thoughtcrime IS death. Produces 100 raw data/sec + 500 storage',
      progressionRequirements: {
        upgrades: ['surveillance']
      },
      failureRate: 0.03
    }),

    new StorageEntity({
      id: 'database',
      name: 'Database',
      cost: 100,
      consumes: { money: 0.4 },
      storage: { rawData: 200, cleanData: 100 },
      description: 'Relational database for storing raw (+200) and clean data (+100). Required for selling data to generate revenue',
      progressionRequirements: {
        upgrades: ['dataStorage']
      },
      failureRate: 0.01,
      maxCount: 3
    }),

    new StorageEntity({
      id: 'dataLake',
      name: 'Data Lake',
      cost: 500,
      consumes: { money: 0.6 },
      storage: { rawData: 1000 },
      description: 'Scalable storage for raw data (+1000 capacity). Prevents data loss when collection exceeds storage limits',
      progressionRequirements: {
        upgrades: ['dataStorage', 'distributedStorage'],
        personnel: { dataEngineer: 1 }
      },
      failureRate: 0.005
    }),

    new StorageEntity({
      id: 'dataWarehouse',
      name: 'Data Warehouse',
      cost: 500,
      consumes: { money: 0.6 },
      storage: { cleanData: 1000 },
      description: 'Optimized storage for clean data (+1000 capacity). Essential for scaling data processing operations',
      progressionRequirements: {
        upgrades: ['dataStorage', 'distributedStorage'],
        personnel: { dataEngineer: 1 }
      },
      failureRate: 0.005
    }),

    new StorageEntity({
      id: 'dataLakehouse',
      name: 'Data Lakehouse',
      cost: 1000,
      consumes: { money: 1.0 },
      storage: { rawData: 10000, cleanData: 5000 },
      description: 'Hybrid architecture combining data lake and warehouse. Massive storage for both raw (+10k) and clean data (+5k)',
      progressionRequirements: {
        upgrades: ['dataStorage', 'distributedStorage'],
        personnel: { dataEngineer: 2 }
      },
      failureRate: 0.005
    }),

    new StorageEntity({
      id: 'featureStore',
      name: 'Feature Store',
      cost: 1000,
      consumes: { money: 1.2 },
      storage: { feature: 50, model: 10 },
      description: 'Centralized repository for ML features (+50) and models (+10). Critical for ML pipeline efficiency',
      progressionRequirements: {
        personnel: { mlEngineer: 1 },
        upgrades: ['featureEngineering']
      },
      failureRate: 0.03
    }),

    new InfrastructureEntity({
      id: 'modelApi',
      name: 'Model API',
      cost: 5000,
      description: 'Premium ML model API service. Generates $20/sec revenue. Consumes 0.2 models/sec',
      consumes: { money: 1.0, model: 0.2 },
      produces: { money: 20 },
      progressionRequirements: {
        upgrades: ['modelTraining'],
        personnel: { mlEngineer: 1 },
        infrastructure: { modelRegistry: 1 }
      },
      failureRate: 0.020
    }),

    new PipelineEntity({
      id: 'etlPipeline',
      name: 'ETL Pipeline',
      cost: 500,
      description: 'Extract, Transform, Load pipeline. Converts 4 raw data → 8 clean data/sec. Requires Data Engineers',
      consumes: { money: 0.8, rawData: 4 },
      produces: { cleanData: 8 },
      progressionRequirements: {
        upgrades: ['hr'],
        personnel: { dataEngineer: 1 }
      },
      failureRate: 0.015
    }),

    new PipelineEntity({
      id: 'featurePipeline',
      name: 'Feature Pipeline',
      cost: 1500,
      description: 'Automated feature engineering. Converts 3 clean data → 2 features/sec. Requires ML Engineers',
      consumes: { money: 1.0, cleanData: 3 },
      produces: { feature: 2 },
      progressionRequirements: {
        personnel: { mlEngineer: 1 },
        upgrades: ['ml']
      },
      failureRate: 0.03
    }),

    new PipelineEntity({
      id: 'streamingSystem',
      name: 'Streaming Pipeline',
      cost: 1000,
      description: 'Real-time data processing. Converts 5 raw data → 10 clean data/sec. High-efficiency pipeline',
      consumes: { money: 1.2, rawData: 5 },
      produces: { cleanData: 10 },
      progressionRequirements: {
        personnel: { dataEngineer: 1 },
        infrastructure: { dataLake: 1 },
        upgrades: ['streaming']
      },
      failureRate: 0.025
    }),

    new PipelineEntity({
      id: 'trainingPipeline',
      name: 'Training Pipeline',
      cost: 3000,
      description: 'Automated ML model training. Converts 3 features → 0.5 models/sec. Critical for ML production',
      consumes: { money: 2.0, feature: 3 },
      produces: { model: 0.5 },
      progressionRequirements: {
        personnel: { mlEngineer: 1 },
        upgrades: ['ml', 'modelTraining']
      },
      failureRate: 0.04
    }),

    new StorageEntity({
      id: 'modelRegistry',
      name: 'Model Registry',
      cost: 2500,
      consumes: { money: 1.5 },
      storage: { model: 20 },
      description: 'Model versioning and deployment system. Stores up to 20 trained models. Required for Model API',
      progressionRequirements: {
        personnel: { mlEngineer: 1 },
        upgrades: ['ml', 'featureEngineering']
      },
      failureRate: 0.03
    }),

    new InfrastructureEntity({
      id: 'reasoningInferenceStorage',
      name: 'Reasoning Inference',
      cost: 8000,
      consumes: { money: 2.0 },
      storage: { reasoningEngine: 50 },
      description: 'Advanced storage for reasoning engines (+50 capacity). Required for AGI development',
      progressionRequirements: {
        personnel: { reasoningSpecialist: 1 },
        upgrades: ['reasoningSystems']
      },
      failureRate: 0.03
    }),

    new InfrastructureEntity({
      id: 'agiCluster',
      name: 'AGI Cluster',
      cost: 100000,
      description: 'Artificial General Intelligence development cluster - the ultimate achievement requiring massive resources',
      consumes: { money: 50, model: 1, reasoningEngine: 1, researchProgress: 5 },
      produces: { agiScore: 0.1 },
      progressionRequirements: {
        personnel: { agiSafetyResearcher: 3 },
        upgrades: ['agiSafety', 'agi']
      },
      failureRate: 0.05,
      maxCount: 1
    }),
  ];
} 